import { productImageAlt } from "@/lib/imageAlt";
import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { fetchProducts, ShopifyProduct, formatPrice, getShopifyImageUrl } from "@/lib/shopify";
import { complementTypes } from "@/lib/complements";
import { exactTypeClause, keepExactTypes } from "@/lib/productTypes";
import { firstAvailableVariant } from "@/lib/variants";
import { cleanProductTitle } from "@/lib/productTitle";
import { useCartStore, CartItem } from "@/stores/cartStore";
import { toast } from "sonner";
import { FREE_SHIPPING_THRESHOLD } from "@/lib/shipping";

interface CartUpsellProps {
  cartItems: CartItem[];
}

export const CartUpsell = ({ cartItems }: CartUpsellProps) => {
  const [suggestions, setSuggestions] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const addItem = useCartStore((state) => state.addItem);
  const isAdding = useCartStore((state) => state.isLoading);
  const subtotal = cartItems.reduce((s, i) => s + parseFloat(i.price.amount) * i.quantity, 0);
  const gap = subtotal > 0 && subtotal < FREE_SHIPPING_THRESHOLD ? FREE_SHIPPING_THRESHOLD - subtotal : 0;


  useEffect(() => {
    const loadSuggestions = async () => {
      if (cartItems.length === 0) {
        setSuggestions([]);
        setLoading(false);
        return;
      }

      try {
        // Pieces that go with the cart's main item (the pairing table the PDP's
        // bundle uses), in stock and priced no higher than it. Random furniture
        // offered a $755 light beside a $245 chair, sometimes sold out.
        const anchor = [...cartItems].sort((a, b) => parseFloat(b.price.amount) - parseFloat(a.price.amount))[0];
        const anchorPrice = parseFloat(anchor.price.amount);
        const types = complementTypes(anchor.product.node.title, anchor.product.node.productType);
        if (!types.length) {
          setSuggestions([]);
          return;
        }
        // Under the free-shipping line, allow pieces just big enough to cross it,
        // otherwise a $60 lamp could never be offered anything that unlocks it.
        const cap = gap > 0 ? Math.max(anchorPrice, gap + 40) : anchorPrice;
        // One small request per type: a single OR query comes back mostly as
        // whichever type is largest (2,000+ throw pillows).
        const products = (
          // Each type exactly (exactTypeClause): "Benches" also matched Shower Benches.
          await Promise.all(
            types.slice(0, 4).map((t) =>
              fetchProducts(12, `${exactTypeClause(t)} AND available_for_sale:true AND variants.price:<=${Math.ceil(cap)}`).then((list) => keepExactTypes(list, [t])),
            ),
          )
        ).flat();
        const cartHandles = new Set(cartItems.map((item) => item.product.node.handle));
        const fits = products.filter((p) => {
          const v = firstAvailableVariant(p.node);
          const price = v ? parseFloat(v.price.amount) : 0;
          return !cartHandles.has(p.node.handle) && v && price > 0 && price <= cap;
        });
        // Under the free-shipping line, the cheapest piece that crosses it comes first.
        const priceOf = (p: ShopifyProduct) => parseFloat(firstAvailableVariant(p.node)!.price.amount);
        const ranked = gap > 0
          ? [...fits].sort((a, b) => {
              const ca = priceOf(a) >= gap, cb = priceOf(b) >= gap;
              if (ca !== cb) return ca ? -1 : 1;
              return priceOf(a) - priceOf(b);
            })
          : fits;
        // One per complementary type.
        const picks: ShopifyProduct[] = [];
        for (const p of ranked) {
          if (picks.some((x) => x.node.productType === p.node.productType)) continue;
          picks.push(p);
          if (picks.length === 3) break;
        }
        setSuggestions(picks);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    loadSuggestions();
  }, [cartItems]);

  const handleAddToCart = async (product: ShopifyProduct) => {
    // The in-stock variant: the first one may be sold out, and checkout drops those.
    const variant = firstAvailableVariant(product.node);
    if (!variant) return;

    const result = await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });

    if (result.ok && !result.notice) toast.success("Added to cart", { position: "top-center" });
  };

  if (loading || suggestions.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        You might also like
      </p>
      <div className="space-y-2">
        {suggestions.map((product) => (
          <div
            key={product.node.id}
            className="flex items-center gap-3 p-2 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
          >
            <div className="w-12 h-12 bg-sand rounded-lg overflow-hidden flex-shrink-0">
              {product.node.images?.edges?.[0]?.node && (
                <img
                  src={getShopifyImageUrl(product.node.images.edges[0].node.url, 600)}
                  alt={productImageAlt(product.node.title, { productType: product.node.productType })}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium truncate">{cleanProductTitle(product.node.title, product.node.productType)}</p>
              <p className="text-[11px] text-primary font-semibold">
                {(() => {
                  const v = firstAvailableVariant(product.node);
                  return v ? formatPrice(v.price.amount, v.price.currencyCode) : null;
                })()}
                {gap > 0 && parseFloat(firstAvailableVariant(product.node)?.price.amount || "0") >= gap && (
                  <span className="ml-2 text-[10px] font-medium text-muted-foreground">· unlocks free shipping</span>
                )}
              </p>
            </div>
            <button
              onClick={() => handleAddToCart(product)}
              disabled={isAdding}
              aria-label={`Add ${cleanProductTitle(product.node.title, product.node.productType)} to cart`}
              className="w-9 h-9 rounded-full flex items-center justify-center border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors flex-shrink-0"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
